import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu, ChevronRight,
  LayoutDashboard, HardHat, Shield, Headphones, Building2, Package,
  Phone, Clock, DollarSign, CheckCircle, ArrowRight, Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Workflows = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [workflows, setWorkflows] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [activeIndustry, setActiveIndustry] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchWorkflows();
  }, [activeIndustry]);

  const fetchWorkflows = async () => {
    try {
      const url = activeIndustry === 'all' ? `${API}/workflows` : `${API}/workflows?industry=${activeIndustry}`;
      const res = await axios.get(url);
      setWorkflows(res.data);
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: MessageSquare, label: 'Pitch Generator', path: '/pitch' },
    { icon: Package, label: 'Workflows', path: '/workflows' },
  ];

  const industries = [
    { id: 'all', name: 'All', icon: Package },
    { id: 'construction', name: 'Construction', icon: HardHat },
    { id: 'insurance', name: 'Insurance', icon: Shield },
    { id: 'healthcare', name: 'Healthcare', icon: Headphones },
    { id: 'realestate', name: 'Real Estate', icon: Building2 },
    { id: 'general', name: 'General', icon: Package },
  ];

  const getIndustryIcon = (industry) => {
    const icons = {
      construction: HardHat,
      insurance: Shield,
      healthcare: Headphones,
      realestate: Building2,
      general: Package
    };
    return icons[industry] || Package;
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />
      
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-500 flex items-center justify-center">
                <Zap className="w-5 h-5 text-black" />
              </div>
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    isActive 
                      ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' 
                      : 'text-muted-foreground hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => { logout(); navigate('/'); }}
              className="w-full justify-start text-muted-foreground hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white">
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-bold">Workflow Templates</h1>
            <span className="text-xs font-mono px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              {Object.keys(workflows).length} WORKFLOWS
            </span>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-6">
          {/* Industry filter */}
          <div className="flex flex-wrap gap-2">
            {industries.map((ind) => {
              const Icon = ind.icon;
              return (
                <button
                  key={ind.id}
                  onClick={() => setActiveIndustry(ind.id)}
                  className={`flex items-center gap-2 px-4 py-2 border transition-all ${
                    activeIndustry === ind.id
                      ? 'bg-emerald-500 text-black border-emerald-500 font-bold'
                      : 'bg-black/40 border-white/10 text-muted-foreground hover:border-emerald-500/50 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {ind.name}
                </button>
              );
            })}
          </div>

          {/* Workflow grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(workflows).map(([id, workflow]) => {
              const IndustryIcon = getIndustryIcon(workflow.industry);
              const isVoice = id.includes('voice') || id.includes('receptionist');
              
              return (
                <div 
                  key={id}
                  className={`bg-[#0a0a0a] border p-6 hover:border-emerald-500/30 transition-all cursor-pointer group relative ${
                    isVoice ? 'border-violet-500/30' : 'border-white/5'
                  }`}
                  onClick={() => setSelectedWorkflow(selectedWorkflow === id ? null : id)}
                  data-testid={`workflow-card-${id}`}
                >
                  {isVoice && (
                    <div className="absolute -top-2 -left-2 px-2 py-0.5 bg-violet-500 text-white text-xs font-bold flex items-center gap-1">
                      <Phone className="w-3 h-3" /> VOICE AI
                    </div>
                  )}
                  
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-10 h-10 flex items-center justify-center ${isVoice ? 'bg-violet-500/10' : 'bg-emerald-500/10'}`}>
                      <IndustryIcon className={`w-5 h-5 ${isVoice ? 'text-violet-400' : 'text-emerald-400'}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold group-hover:text-emerald-400 transition-colors">{workflow.name}</h3>
                      <p className="text-xs text-muted-foreground uppercase">{workflow.industry}</p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-4">{workflow.description}</p>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Clock className="w-4 h-4" /> {workflow.estimated_time_saved}
                    </span>
                    <span className="font-mono text-emerald-400">{workflow.suggested_price}</span>
                  </div>

                  {/* Expanded details */}
                  {selectedWorkflow === id && (
                    <div className="mt-6 pt-6 border-t border-white/10 space-y-4 animate-fade-in-up">
                      <div>
                        <h4 className="text-sm font-bold mb-2">Workflow Steps</h4>
                        <ol className="space-y-2">
                          {workflow.steps.map((step, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <span className="w-5 h-5 bg-emerald-500/10 text-emerald-400 flex items-center justify-center flex-shrink-0 text-xs font-mono">{i + 1}</span>
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-bold mb-2">Tools Required</h4>
                        <div className="flex flex-wrap gap-2">
                          {workflow.tools.map((tool, i) => (
                            <span key={i} className="px-2 py-1 bg-white/5 text-xs text-muted-foreground">
                              {tool}
                            </span>
                          ))}
                        </div>
                      </div>

                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/jobs', { state: { searchQuery: workflow.name.split(' ')[0] } });
                        }}
                        className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm"
                      >
                        Find Jobs for This Workflow <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {Object.keys(workflows).length === 0 && !loading && (
            <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
              <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-bold mb-2">No workflows found</h3>
              <p className="text-muted-foreground">Try selecting a different industry filter.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Workflows;